import { useState } from "react";
import { Box, Paper } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { format, parse, parseISO } from "date-fns";
import { getQueestionnaireByDateRange } from "../../../services/apiCalls.js";
import QuestionnaireModal from "./QuestionnaireModal.jsx";

const queCodeNameMapping = {
  // Legacy codenames
  NUTRITION: "Nutrition",
  MET_HOURS: "Metabolic Hours",
  SOCIOECONOMIC: "Socioeconomic",
  HEALTH_LIFESTYLE: "Health Lifestyle",
  // Current codenames
  Nutrition: "Nutrition",
  MetabolicHours: "Metabolic Hours",
  SF: "Socioeconomic",
  "Health-Lifestyle": "Health Lifestyle",
};

export default function QuestionnaireTable({
  questionnaireCodeNameDate,
  selectedUser,
  loadingAvailableQuestionnaires,
}) {
  const [questionnaireQuestions, setQuestionnaireQuestions] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const { t } = useTranslation("questionnaires");

  const transformedData = questionnaireCodeNameDate.map((item) => ({
    id: item.id,
    codeName: queCodeNameMapping[item.codeName] || item.codeName,
    date: format(parseISO(item.date), "dd-MM-yyyy"),
  }));

  // console.log("Transformed data in questionnaireTable: ", transformedData);

  const codeNameOptions = Array.from(
    new Set(transformedData.map((item) => item.codeName)),
  );

  const datesOptions = Array.from(
    new Set(transformedData.map((item) => item.date)),
  );

  const columns = [
    {
      field: "codeName",
      headerName: t("columns.title"),
      flex: 1,
      align: "center",
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: codeNameOptions,
    },
    {
      field: "date",
      headerName: t("columns.repliedOn"),
      flex: 1,
      align: "center",
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: datesOptions,
    },
  ];

  const reverseMapping = Object.entries(queCodeNameMapping).reduce(
    (acc, [key, value]) => {
      acc[value] = key;
      return acc;
    },
    {},
  );

  const transformSelectedRow = (selectedRow) => {
    const { id, codeName, date } = selectedRow;

    const mappedCodeName = reverseMapping[codeName] || codeName;

    const parsedDate = parse(date, "dd-MM-yyyy", new Date());
    const formattedDate = format(parsedDate, "yyyy-MM-dd");

    return {
      id,
      codeName: mappedCodeName,
      date: formattedDate,
    };
  };

  const handleRowClick = (params) => {
    const transformedRow = transformSelectedRow(params.row);

    // console.log("TransformedRow: ", transformedRow);
    getQuestionsByDateAndCodeName(transformedRow, selectedUser);

    setOpenModal(true);
  };

  const getQuestionsByDateAndCodeName = async (rowSelected, selectedUser) => {
    try {
      setLoadingQuestions(true);
      const allQuestions = await getQueestionnaireByDateRange(
        rowSelected.date,
        rowSelected.date,
        selectedUser,
      );

      const filteredQuestions = allQuestions
        .filter((item) => item.questionnaireCodename === rowSelected.codeName)
        .map((item, index) => ({
          ...item,
          no: index + 1,
          score: item.score ? item.score : "No score",
          questionTitle: item.questionTitle.replace(/<\/?p>/g, ""),
          questionCodeName: queCodeNameMapping[item.codeName] || item.codeName,
          date: format(parseISO(item.date), "dd-MM-yyyy"),
        }));

      setQuestionnaireQuestions(filteredQuestions);
      // console.log("filteredQuestions: ", filteredQuestions);
    } catch (error) {
      // console.log(
      //   "Error when fetching questions by date range in QuestionnaireTable component: ",
      //   error
      // );
    } finally {
      setLoadingQuestions(false);
    }
  };

  return (
    <Box sx={{ margin: "40px 30px" }}>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <>
          <DataGrid
            rows={transformedData}
            columns={columns}
            filterMode="client" // Enables filtering
            disableColumnMenu={false}
            onRowClick={handleRowClick}
            loading={loadingAvailableQuestionnaires}
            sx={{
              cursor: "pointer",
              "& .MuiDataGrid-row:hover": {
                bgcolor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            pagination
            paginationModel={{
              pageSize: rowsPerPage,
              page: page,
            }}
            onPaginationModelChange={({ page, pageSize }) => {
              setPage(page);
              setRowsPerPage(pageSize);
            }}
            pageSizeOptions={[5, 10, 20, 50]}
          />
        </>
      </Paper>
      <QuestionnaireModal
        open={openModal}
        loadingQuestions={loadingQuestions}
        setOpen={setOpenModal}
        questionnaireQuestions={questionnaireQuestions}
      />
    </Box>
  );
}
